import {
  ResourcesComponent,
  RESOURCES_COMPONENT_TOKEN,
  IGameConfigProvider,
  T_IGameConfigProvider,
  TimeScaleComponent,
  TIME_SCALE_COMPONENT_TOKEN,
  FastSpinsComponent,
  FAST_SPINS_COMPONENT,
} from '@cgs/machines';
import { Container, SceneObject } from '@cgs/wge';
import {
  TRIFECTA_SDK_ADAPTER_TOKEN,
  TrifectaSDKAdapter,
  TrifectaSDKTurboSpinMode,
} from '@cgs/provider-trifecta';

interface SpeedPreset {
  timeScale: number;
}

// Presets follow prod/trifecta: x1 = prod base speed, x4 = prod fast spins,
// x2 sits between the two. Reel speed multipliers for x2/x4 come separately
// through the game hook fastSpins params -> FastSpinsComponent.
const BASE_SPEED_PRESET: SpeedPreset = { timeScale: 1 };
const SPEED_PRESETS = new Map<TrifectaSDKTurboSpinMode, SpeedPreset>([
  [TrifectaSDKTurboSpinMode.x1, BASE_SPEED_PRESET],
  [TrifectaSDKTurboSpinMode.x2, { timeScale: 1.1 }],
  [TrifectaSDKTurboSpinMode.x4, { timeScale: 1.2 }],
]);
const FALLBACK_FAST_PRESET = SPEED_PRESETS.get(TrifectaSDKTurboSpinMode.x4)!;

export const SANTA_SKIP_ANIMATIONS_COMPONENT_TOKEN = Symbol(
  'SANTA_SKIP_ANIMATIONS_COMPONENT_TOKEN'
);

export class SantaSkipAnimationsComponent extends SceneObject {
  private readonly timeScaleComponent: TimeScaleComponent;
  private readonly fastSpinsComponent: FastSpinsComponent;
  private readonly trifectaSDKAdapter: TrifectaSDKAdapter | null;
  private readonly collapsingParameters: any[];
  private readonly baseCollapseMultipliers: number[];

  private appliedPreset: SpeedPreset | null = null;

  constructor(container: Container) {
    super();

    this.touchable = false;
    this.visible = false;

    this.initialize();

    const resourcesComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    resourcesComponent.root.addChild(this);

    this.timeScaleComponent = container.resolve<TimeScaleComponent>(TIME_SCALE_COMPONENT_TOKEN);
    this.fastSpinsComponent = container.resolve<FastSpinsComponent>(FAST_SPINS_COMPONENT);
    this.trifectaSDKAdapter = container.resolve<TrifectaSDKAdapter>(TRIFECTA_SDK_ADAPTER_TOKEN, {
      optional: true,
    });

    const { parameters, baseMultipliers } = this.resolveCollapsingParameters(container);
    this.collapsingParameters = parameters;
    this.baseCollapseMultipliers = baseMultipliers;

    this.applyPreset(BASE_SPEED_PRESET);
  }

  public override update(dt: number): void {
    super.update(dt);

    const preset = this.currentPreset();

    if (preset !== this.appliedPreset) {
      this.applyPreset(preset);
    }
  }

  public override dispose(): void {
    this.applyPreset(BASE_SPEED_PRESET);
    super.dispose();
  }

  private currentPreset(): SpeedPreset {
    const turboSpinMode = this.trifectaSDKAdapter?.config?.turboSpinMode as
      | TrifectaSDKTurboSpinMode
      | undefined;

    if (turboSpinMode !== undefined) {
      return SPEED_PRESETS.get(turboSpinMode) ?? BASE_SPEED_PRESET;
    }

    return this.fastSpinsComponent.isFastSpinsActive ? FALLBACK_FAST_PRESET : BASE_SPEED_PRESET;
  }

  private applyPreset(preset: SpeedPreset): void {
    this.appliedPreset = preset;
    this.timeScaleComponent.timeScale = preset.timeScale;

    this.collapsingParameters.forEach((parameters, index) => {
      const baseMultiplier = this.baseCollapseMultipliers[index] ?? 1;
      parameters.spinSpeedMultiplier = Math.max(baseMultiplier, preset.timeScale);
    });
  }

  private resolveCollapsingParameters(container: Container): {
    parameters: any[];
    baseMultipliers: number[];
  } {
    try {
      const provider = container.resolve<IGameConfigProvider>(T_IGameConfigProvider, {
        optional: true,
      });
      if (!provider) {
        return { parameters: [], baseMultipliers: [] };
      }

      const gameConfig: any = provider.gameConfig;
      const parameters: any[] = [];
      const baseMultipliers: number[] = [];
      const collect = (configKey: string) => {
        const collapsingParameters = gameConfig?.[configKey]?.collapsingParameters;
        if (collapsingParameters) {
          parameters.push(collapsingParameters);
          baseMultipliers.push(collapsingParameters.spinSpeedMultiplier ?? 1);
        }
      };

      collect('regularSpinCollapsingConfig');
      collect('freeSpinCollapsingConfig');

      return { parameters, baseMultipliers };
    } catch {
      return { parameters: [], baseMultipliers: [] };
    }
  }
}
